import React from 'react';
import { useForm } from 'react-hook-form';

import { TrackableFile } from 'types/trackables';

import { useUpdateTrackableFile } from '../hooks/useUpdateTrackableFile';

import { Drawer, DrawerProps } from 'components/Drawer/DrawerDeprecated';
import { Text } from 'components/Text/Text';
import { Button } from 'components/Button/Button';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';

export type EditTrackableFileDialogProps = DrawerProps & {
  trackableFile: TrackableFile;
  trackableListId: string;
  trackableItemId: string;
  onSuccess?: () => void;
};

interface EditTrackableFileFormData {
  description: string;
}

export function EditTrackableFileDialog({
  trackableFile,
  trackableListId,
  trackableItemId,
  open,
  onClose,
  onSuccess,
}: EditTrackableFileDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<EditTrackableFileFormData>({
    defaultValues: {
      description: trackableFile.description || '',
    },
  });

  const updateTrackableFile = useUpdateTrackableFile({
    onSuccess: () => {
      onClose?.();
      onSuccess?.();
      reset();
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    await updateTrackableFile.mutateAsync({
      trackable_list_id: trackableListId,
      trackable_item_id: trackableItemId,
      trackable_file_id: trackableFile.id,
      data: {
        description: data.description || null,
      },
    });
  });

  React.useEffect(() => {
    if (open) {
      reset({
        description: trackableFile.description || '',
      });
    }
  }, [open, trackableFile.description, reset]);

  return (
    <Drawer title="Edit Image" open={open} onClose={onClose}>
      <Text type="paragraph">
        Edit the description for the image "{trackableFile.name}".
      </Text>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <Field label="Description">
          <Input
            placeholder="Enter image description..."
            {...register('description')}
          />
        </Field>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Save Changes
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
